import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import './RichText.scss';

// Dynamic import for DOMPurify to handle SSR
let DOMPurify = null;
if (typeof window !== 'undefined') {
  import('dompurify').then((module) => {
    DOMPurify = module.default;
  });
}

/**
 * Rich Text Component
 *
 * A component for rendering rich HTML content safely with consistent typography.
 * Sanitizes HTML content to prevent XSS attacks while preserving formatting.
 *
 * Based on ESS Design System requirements:
 * - Safe HTML rendering with DOMPurify sanitization
 * - Consistent typography using design tokens
 * - Responsive text scaling
 * - Accessible markup and proper heading hierarchy
 */
const RichText = ({
  content,
  className,
  as: Component = 'div',
  sanitize = true,
  allowedTags,
  allowedAttributes,
  alignment = 'left',
  withVerticalSpacing = true,
  ...props
}) => {
  // Sanitization configuration
  const sanitizeConfig = {
    ALLOWED_TAGS: allowedTags || [
      'p',
      'b',
      'br',
      'strong',
      'em',
      'u',
      's',
      'sub',
      'sup',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'pre',
      'code',
      'a',
      'img',
      'figure',
      'figcaption',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'dl',
      'dt',
      'dd',
      'iframe',
      'div',
      'span',
      'hr',
      'cite',
    ],
    ALLOWED_ATTR: allowedAttributes || [
      'href',
      'title',
      'alt',
      'src',
      'width',
      'height',
      'class',
      'id',
      'target',
      'rel',
      'data-label', // For responsive table headers
      'frameborder', // For iframes
      'allowfullscreen', // For video iframes
      'allow', // For iframe permissions
      'loading', // For lazy loading
    ],
    ALLOW_DATA_ATTR: true, // Allow data attributes for responsive tables
    ADD_ATTR: ['target', 'rel'],
    TRANSFORM_TAGS: {
      // Ensure external links have proper security attributes
      a: function (tagName, attribs) {
        if (
          attribs.href &&
          (attribs.href.startsWith('http') || attribs.href.startsWith('//'))
        ) {
          attribs.target = '_blank';
          attribs.rel = 'noopener noreferrer';
        }
        return { tagName, attribs };
      },
    },
  };

  // State for processed content to handle SSR
  const [processedContent, setProcessedContent] = useState(content);
  const containerRef = useRef(null);

  // Effect to sanitize content on client-side
  useEffect(() => {
    if (sanitize && content && DOMPurify) {
      setProcessedContent(DOMPurify.sanitize(content, sanitizeConfig));
    } else {
      setProcessedContent(content);
    }
  }, [content, sanitize]);

  // Effect to process tables for DataTable styling + responsive mobile layout
  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) return;

    const tables = containerRef.current.querySelectorAll('table');
    tables.forEach((table) => {
      // Wrap with the same container classes DataTable uses
      if (!table.parentElement?.classList.contains('table-container')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'table-container table-responsive';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }

      table.querySelectorAll('th').forEach((th) => {
        th.classList.add('table-head');
      });
      table.querySelectorAll('td').forEach((td) => {
        td.classList.add('table-cell');
      });

      const headers = [];
      const theadRow = table.querySelector('thead tr');
      let headerRow = theadRow;
      let bodyRows = [];

      if (theadRow) {
        theadRow.querySelectorAll('th, td').forEach((cell) => {
          headers.push(cell.textContent.trim());
        });
        bodyRows = Array.from(table.querySelectorAll('tbody tr'));
      } else {
        // CMS tables often lack thead — treat the first row as header
        headerRow = table.querySelector('tr');
        if (headerRow) {
          table.classList.add('ess-table--first-row-header');
          headerRow.classList.add('ess-table__header-row');
          headerRow.querySelectorAll('th, td').forEach((cell) => {
            headers.push(cell.textContent.trim());
            cell.classList.add('table-head');
          });
          bodyRows = Array.from(table.querySelectorAll('tr')).filter(
            (row) => row !== headerRow,
          );
        }
      }

      if (headers.length === 0) return;

      bodyRows.forEach((row) => {
        row.querySelectorAll('td').forEach((td, index) => {
          if (headers[index]) {
            td.setAttribute('data-label', headers[index]);
          }
        });
      });
    });
  }, [processedContent]);

  // Fallback for SSR - use unsanitized content temporarily
  const finalContent =
    typeof window === 'undefined' ? content : processedContent;

  // Base CSS classes
  const baseClasses = cx(
    'ess-rich-text',
    `ess-rich-text--${withVerticalSpacing ? 'with-v-spacing' : 'no-v-spacing'}`,
    `ess-rich-text--${alignment}-aligned`,
    className,
  );

  return (
    <Component
      ref={containerRef}
      className={baseClasses}
      dangerouslySetInnerHTML={{ __html: finalContent }}
      {...props}
    />
  );
};

RichText.propTypes = {
  /** HTML content to render */
  content: PropTypes.string.isRequired,

  /** Additional CSS classes */
  className: PropTypes.string,

  /** HTML element type to render */
  as: PropTypes.elementType,

  /** Whether to sanitize HTML content (recommended) */
  sanitize: PropTypes.bool,

  /** Custom allowed HTML tags (overrides default) */
  allowedTags: PropTypes.arrayOf(PropTypes.string),

  /** Custom allowed HTML attributes (overrides default) */
  allowedAttributes: PropTypes.arrayOf(PropTypes.string),

  /** Text alignment */
  alignment: PropTypes.oneOf(['left', 'center', 'right']),

  /** Whether to add vertical spacing between paragraphs and elements */
  withVerticalSpacing: PropTypes.bool,
};

export default RichText;
